import type {LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import AOS from 'aos'
import 'aos/dist/aos.css'
import cn from 'classnames'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import type {
  AllDocumentTypes,
  ArticleDocumentData,
  ButtonDocumentData,
  CustomerStoryDocumentData,
  HomePageDocumentData,
  NavigationDocumentData,
  PageDocumentData,
  SubNavBannerDocumentData,
} from 'prismicio-types'
import type {ReactNode} from 'react'
import {memo} from 'react'
import {useCookie, useEffectOnce} from 'react-use'
import type {ParentUid} from 'src/common/utils/documentHelpers'
import {DialogContextProvider} from 'src/context/DialogContext'
import {DrawerContextProvider} from 'src/context/DrawerContext'
import {HeaderContextProvider} from 'src/context/HeaderContext'
import {NotificationContextProvider} from 'src/context/NotificationContext'
import {PageContextProvider} from 'src/context/PageContext'
import {FooterV2} from './FooterV2'
import {HeaderV2} from './HeaderV2'
import {HrefLangElements} from './HrefElements'
import {NotificationBanner} from './NotificationBanner'
import {SubNavBanner} from './SubNavBanner'

const HeaderV3 = dynamic(() => import('./HeaderV3').then((mod) => mod.HeaderV3), {
  ssr: false,
})

function getBackgroundStyle(props: {
  customBackground?: string
  desktopBackground?: string
  mobileBackground?: string
}): React.CSSProperties | undefined {
  if (props.customBackground) {
    return {'--custom-bg': `url(${props.customBackground})`} as React.CSSProperties
  }

  if (props.desktopBackground || props.mobileBackground) {
    const desktop = props.desktopBackground || props.mobileBackground
    const mobile = props.mobileBackground || props.desktopBackground

    return {
      '--bg-desktop': `url(${desktop})`,
      '--bg-mobile': `url(${mobile})`,
    } as React.CSSProperties
  }

  return undefined
}

export const Layout = memo(function (props: {
  alternateLanguages: Array<AllDocumentTypes['alternate_languages'][number] & {parentUid?: string}>
  breadcrumbs?:
    | PageDocumentData['breadcrumbs']
    | ArticleDocumentData['breadcrumbs']
    | CustomerStoryDocumentData['breadcrumbs']
    | HomePageDocumentData['breadcrumbs']
  children: ReactNode
  className?: string
  navigationData: NavigationDocumentData
  mainClassName?: string
  hideNavigation?: boolean
  isDarkNavigation?: boolean
  pageUid?: string
  locale?: string
  parentUids?: ParentUid[]
  forceFooterV2?: boolean
  funnelType?: string | null
  canonicalUrl?: LinkField
  customBackground?: string
  desktopBackground?: string
  mobileBackground?: string
  hideFooter?: boolean
  hideHeader?: boolean
  primaryNavButton?: ButtonDocumentData | null
  secondaryNavButton?: ButtonDocumentData | null
  hidePrimaryNavButton?: boolean
  hideSecondaryNavButton?: boolean
  primaryNavButtonScrollText?: string | null
  primaryNavButtonScrollId?: string | null
  secondaryNavButtonScrollText?: string | null
  secondaryNavButtonScrollId?: string | null
}) {
  const isProduction = typeof window !== 'undefined' && window.location.host.includes('getmoss.com')
  const hasParentPageUid = props.parentUids?.some((el) => el.lang === props.locale)
  const parentUid = hasParentPageUid ? props.parentUids?.find((el) => el.lang === props.locale)?.uid : null
  const isNavV3Enabled = props.navigationData.enable_navbar_v3
  const Header = isNavV3Enabled ? HeaderV3 : HeaderV2
  const filteredLanguages = props.alternateLanguages

  const [internalUserCookie, updateInternalUserCookie] = useCookie('internal_user_id')

  // Initiate AOS
  useEffectOnce(() => {
    if (!internalUserCookie) {
      updateInternalUserCookie('true')
    }

    AOS.init({
      duration: 700,
    })
  })

  const hasResponsiveBackground = Boolean(props.customBackground || props.desktopBackground || props.mobileBackground)

  return (
    <NotificationContextProvider
      showOnAllPages={props.navigationData?.show_on_all_pages !== false}
      includedUids={props.navigationData?.included_uids}
      includedPatterns={props.navigationData?.included_patterns}
      notificationId={props.navigationData?.notification_id}
      excludedUids={props.navigationData?.excluded_ids}
      excludedPatterns={props.navigationData?.excluded_patterns}
      pageUid={props.pageUid}
    >
      {!props.hideNavigation && props.navigationData.notification_text && (
        <NotificationBanner
          message={props.navigationData.notification_text}
          linkField={props.navigationData.notification_link}
          linkText={props.navigationData.notification_link_label}
          dataType={props.navigationData.data_type}
          notificationId={props.navigationData.notification_id}
          pageName={props.pageUid}
          images={props.navigationData.notification_image}
          ignoreLocale={props.navigationData.ignore_locale_in_notification_link}
        />
      )}
      <div
        className={cn(
          'relative grid min-h-screen grid-rows-[auto_1fr_auto]',
          {'bg-f8f8f8': !hasResponsiveBackground && !props.hideFooter},
          {'bg-white': !hasResponsiveBackground && props.hideFooter},
          {
            'bg-[image:var(--custom-bg)] bg-repeat': props.customBackground,
            'bg-[length:100%] sm:bg-[length:100px_auto]': props.customBackground,
            'bg-[position:top_center]': props.customBackground,
          },
          {
            'bg-[image:var(--bg-mobile)] sm:bg-[image:var(--bg-desktop)] bg-cover bg-no-repeat bg-[position:top_center]':
              props.desktopBackground || props.mobileBackground,
          },
          props.className,
        )}
        style={getBackgroundStyle(props)}
      >
        <HrefLangElements
          alternateLanguages={filteredLanguages}
          parentUid={parentUid}
          pageUid={props.pageUid}
          locale={props.locale}
          canonicalUrl={props.canonicalUrl}
        />
        {typeof window !== 'undefined' && (
          <Head>
            {isProduction && (
              <>
                <script
                  dangerouslySetInnerHTML={{
                    __html: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag() {
                        dataLayer.push(arguments);
                    }

                    gtag("consent", "default", {
                        ad_personalization: "denied",
                        ad_storage: "denied",
                        ad_user_data: "denied",
                        analytics_storage: "denied",
                        wait_for_update: 2000 // milliseconds to wait for update
                    });

                    gtag("set", "ads_data_redaction", true);

                    (function(w,d,s,l){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.getmoss.com/bluepanda/qteJVDzkwgAjlh/';f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer');
                  `,
                  }}
                />
              </>
            )}
          </Head>
        )}
        <DialogContextProvider>
          <DrawerContextProvider>
            <PageContextProvider funnelType={props.funnelType}>
              <HeaderContextProvider>
                {!props.hideHeader && (
                  <Header
                    navigationData={props.navigationData}
                    isDarkNavigation={props.isDarkNavigation}
                    key={Date.now()}
                    pageUid={props.pageUid}
                    primaryNavButton={props.primaryNavButton}
                    secondaryNavButton={props.secondaryNavButton}
                    hidePrimaryNavButton={props.hidePrimaryNavButton}
                    hideSecondaryNavButton={props.hideSecondaryNavButton}
                    primaryNavButtonScrollText={props.primaryNavButtonScrollText}
                    primaryNavButtonScrollId={props.primaryNavButtonScrollId}
                    secondaryNavButtonScrollText={props.secondaryNavButtonScrollText}
                    secondaryNavButtonScrollId={props.secondaryNavButtonScrollId}
                    hideNavLinks={props.hideNavigation}
                  />
                )}
                {isFilled.contentRelationship(props.navigationData.sub_nav_banner) &&
                props.navigationData.sub_nav_banner.data ? (
                  <SubNavBanner
                    data={props.navigationData.sub_nav_banner.data as SubNavBannerDocumentData}
                    pageUid={props.pageUid}
                  />
                ) : null}
                <main className={cn(props.mainClassName, {'mt-[-60px]': !props.hideHeader}, 'min-w-0')}>
                  {props.children}
                </main>

                {!props.hideNavigation && !props.hideFooter && (
                  <FooterV2
                    alternateLanguages={filteredLanguages}
                    navigationData={props.navigationData}
                    breadcrumbs={props.breadcrumbs}
                    pageUid={props.pageUid}
                  />
                )}
              </HeaderContextProvider>
            </PageContextProvider>
          </DrawerContextProvider>
        </DialogContextProvider>
        <div id="dialog-container" />
        <div id="drawer-container" />
      </div>
    </NotificationContextProvider>
  )
})
